import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Turnstile } from "@marsidev/react-turnstile";
import { toast } from "sonner";
import { Flag, Award, Heart, ShieldCheck, Upload, Loader2, CheckCircle2, Vote, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { media, useGiveawayState } from "@/lib/useGiveaway";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import Countdown from "@/components/giveaway/Countdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const BRANCHES = ["U.S. Army", "U.S. Navy", "U.S. Air Force", "U.S. Marine Corps", "U.S. Coast Guard", "U.S. Space Force", "National Guard"];

function VoteDialog({ nominee, open, onClose }) {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email || !token) { toast.error("Enter your email and complete verification."); return; }
    setBusy(true);
    try {
      await api.post("/giveaway/vote", { nominee_id: nominee.id, email, turnstile_token: token });
      toast.success("Check your email to confirm your vote!");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit vote.");
      setToken(null);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Vote for {nominee?.first_name}</DialogTitle>
          <DialogDescription>One vote per person. Enter your email — we'll send a link to confirm your vote.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Your email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1" data-testid="vote-email" />
          </div>
          {SITE_KEY && <Turnstile siteKey={SITE_KEY} onSuccess={setToken} onExpire={() => setToken(null)} options={{ theme: "light", action: "vote" }} />}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="vote-submit" className="px-4 py-2 rounded-md bg-launch text-white font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm Vote
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const BLANK = {
  nominee_first_name: "", nominee_last_name: "", city: "", state: "FL", zip: "", branch: "", years_served: "",
  nominator_first_name: "", nominator_last_name: "", nominator_email: "", nominator_phone: "", relationship: "",
  story: "", why: "", hvac_situation: "", photo_path: "", permission_publish: false, agree_truthful: false,
};

function NominateDialog({ open, onClose, onDone }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (open) { setF(BLANK); setPhotoPreview(""); } }, [open]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/giveaway/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_path", data.path); setPhotoPreview(media(data.url));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.permission_publish || !f.agree_truthful) { toast.error("Please confirm the two agreements."); return; }
    setBusy(true);
    try {
      await api.post("/giveaway/nominate", f);
      toast.success("Nomination submitted! Check your email for confirmation.");
      onDone(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please complete all required fields." : (d || "Submission failed."));
    } finally { setBusy(false); }
  };

  const T = ({ k, label, type = "text", area, rows }) => (
    <div>
      <Label className="text-sm text-navy">{label}</Label>
      {area
        ? <Textarea value={f[k]} onChange={(e) => set(k, e.target.value)} rows={rows || 4} className="mt-1" data-testid={`nom-${k}`} />
        : <Input type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} className="mt-1" data-testid={`nom-${k}`} />}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nominate a Veteran</DialogTitle>
          <DialogDescription>All nominations are reviewed and subject to eligibility verification before appearing publicly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs font-bold uppercase tracking-wider text-launch">The Veteran</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <T k="nominee_first_name" label="Veteran first name" />
            <T k="nominee_last_name" label="Veteran last name" />
            <T k="city" label="City" />
            <T k="state" label="State" />
            <T k="zip" label="ZIP code" />
            <div>
              <Label className="text-sm text-navy">Branch of military</Label>
              <select value={f.branch} onChange={(e) => set("branch", e.target.value)} data-testid="nom-branch" className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                <option value="">Select…</option>
                {BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <T k="years_served" label="Approx. years served" />
          </div>

          <p className="text-xs font-bold uppercase tracking-wider text-launch pt-2">About You (the Nominator)</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <T k="nominator_first_name" label="Your first name" />
            <T k="nominator_last_name" label="Your last name" />
            <T k="nominator_email" label="Your email" type="email" />
            <T k="nominator_phone" label="Your phone" />
            <T k="relationship" label="Relationship to veteran" />
          </div>

          <p className="text-xs font-bold uppercase tracking-wider text-launch pt-2">Their Story</p>
          <T k="story" label="Tell us this veteran's story" area rows={5} />
          <T k="why" label="Why should this veteran receive a new HVAC system?" area />
          <T k="hvac_situation" label="Current HVAC situation / problem" area />

          <div>
            <Label className="text-sm text-navy">Optional photo of the veteran</Label>
            <div className="mt-1 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md px-3 py-2 text-sm cursor-pointer" data-testid="nom-photo-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choose Image
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="nom-photo-input" />
              </label>
              {photoPreview && <img src={photoPreview} alt="preview" className="h-14 w-14 rounded object-cover border" />}
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t border-slate-100">
            <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.permission_publish} onChange={(e) => set("permission_publish", e.target.checked)} className="mt-1" data-testid="nom-permission" /> I have permission to share this veteran's story and photo, and consent to Aquila Air &amp; Heating publishing the approved story/photo.</label>
            <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.agree_truthful} onChange={(e) => set("agree_truthful", e.target.checked)} className="mt-1" data-testid="nom-truthful" /> I confirm the information submitted is truthful to the best of my knowledge.</label>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="nom-submit" className="px-4 py-2 rounded-md bg-launch text-white font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit Nomination
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NomineeCard({ n, canVote, onVote }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col" data-testid={`nominee-card-${n.id}`}>
      <div className="h-44 bg-slate-100">
        {n.photo_url ? <img src={media(n.photo_url)} alt={`${n.first_name} — ${n.branch}`} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-slate-300"><Flag className="h-12 w-12" /></div>}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-lg font-bold text-navy">{n.first_name} {n.last_name}</h3>
        <p className="text-sm text-launch font-semibold">{n.branch}{n.city ? ` • ${n.city}, ${n.state}` : ""}</p>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1 line-clamp-5">{n.story}</p>
        {typeof n.votes === "number" && <p className="mt-3 text-sm font-bold text-navy">{n.votes} vote{n.votes === 1 ? "" : "s"}</p>}
        {canVote && (
          <button onClick={() => onVote(n)} data-testid={`vote-btn-${n.id}`} className="mt-3 inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy/90 text-white font-bold py-2.5 rounded-md text-sm transition-colors">
            <Vote className="h-4 w-4" /> Vote for {n.first_name}
          </button>
        )}
      </div>
    </div>
  );
}

export default function VeteranGiveaway() {
  const { data: state } = useGiveawayState();
  const [voteFor, setVoteFor] = useState(null);
  const [nomOpen, setNomOpen] = useState(false);
  const phase = state?.phase || "pre";
  const winnerCard = state?.winner?.winner_card_url;
  const { data: nomData, refetch } = useQuery({
    queryKey: ["giveaway-nominees", phase],
    queryFn: async () => (await api.get("/giveaway/nominees")).data,
    enabled: !!state,
  });
  const { data: rules } = useQuery({ queryKey: ["giveaway-rules"], queryFn: async () => (await api.get("/giveaway/rules")).data });
  const nominees = nomData?.nominees || [];

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("vote");
    if (!p) return;
    const map = { success: ["Your vote is confirmed. Thank you!", "success"], already: ["You've already voted. One vote per person.", "info"], closed: ["Voting is closed.", "info"], invalid: ["That confirmation link is invalid or expired.", "error"] };
    const [msg, kind] = map[p] || [];
    if (msg) toast[kind === "error" ? "error" : kind === "info" ? "message" : "success"](msg);
    window.history.replaceState({}, "", "/veteran-hvac-giveaway");
  }, []);

  const heroTitle = {
    pre: "Coming October 1", teaser: "Something Big Is Coming This Veterans Day",
    voting: "Nominations & Voting Are Open", review: "Voting Has Closed",
    winner: "Our Veteran Giveaway Winner", closed: "Our Veteran Giveaway Winner",
  }[phase];

  return (
    <>
      <Seo
        title="Veteran HVAC Giveaway 2026 | Aquila Air & Heating"
        description="Aquila Air & Heating is giving one brand-new HVAC system with professional installation to a Space Coast veteran in need. Nominate & vote October 1–31, 2026. Winner announced Veterans Day."
        path="/veteran-hvac-giveaway/"
        image={winnerCard ? media(winnerCard) : "/giveaway/hero.jpg"}
        jsonLd={[{ "@context": "https://schema.org", "@type": "Event", name: "Aquila Air & Heating Veteran HVAC Giveaway 2026", startDate: "2026-10-01", endDate: "2026-11-11", eventStatus: "https://schema.org/EventScheduled", description: "A brand-new HVAC system with professional installation given to a veteran in need." }]}
      />

      {/* HERO */}
      <section className="relative bg-[#070d1c] text-white" data-testid="giveaway-hero">
        <div className="absolute inset-0"><img src="/giveaway/hero.jpg" alt="Veteran HVAC Giveaway" className="w-full h-full object-cover opacity-30" /><div className="absolute inset-0 bg-gradient-to-t from-[#070d1c] via-[#070d1c]/80 to-[#070d1c]/40" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pb-20">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Veteran HVAC Giveaway" }]} /></div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-launch"><Flag className="h-4 w-4" /> Honor • Serve • Give Back</p>
          <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">{heroTitle}</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 leading-relaxed">
            Aquila Air &amp; Heating is giving <strong className="text-white">one new HVAC system with professional installation</strong> to a veteran in need on Florida's Space Coast.
          </p>
          {["pre", "teaser"].includes(phase) && (
            <div className="mt-8"><Countdown target={state?.countdown_to} label="Nominations & Voting open in" /></div>
          )}
          {phase === "voting" && (
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button onClick={() => setNomOpen(true)} data-testid="open-nominate" className="inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-lg transition-colors">
                <Heart className="h-5 w-5" /> Nominate a Veteran
              </button>
              <a href="#nominees" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-3.5 px-8 rounded-md transition-colors">
                <Vote className="h-5 w-5" /> Vote Now
              </a>
            </div>
          )}
          {phase === "voting" && <div className="mt-8"><Countdown target={state?.countdown_to} label="Voting closes in" /></div>}
          {phase === "review" && <p className="mt-6 max-w-2xl text-slate-200">Voting is now closed. We're reviewing the results and verifying our winner. The winner will be announced on <strong className="text-white">Veterans Day, November 11</strong>.</p>}
        </div>
      </section>

      {/* WINNER */}
      {["winner", "closed"].includes(phase) && (
        <section className="py-14 md:py-20 bg-white" data-testid="giveaway-winner-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {state?.winner ? (
              <>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-launch">🏆 Happy Veterans Day</p>
                <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-navy">{state.winner.first_name} {state.winner.last_name}</h2>
                <p className="mt-1 text-launch font-semibold">{state.winner.branch}{state.winner.city ? ` • ${state.winner.city}, ${state.winner.state}` : ""}</p>
                {state.winner.photo_url && <img src={media(state.winner.photo_url)} alt="Giveaway winner" className="mt-6 mx-auto rounded-2xl shadow-lg max-h-96 object-cover" />}
                <p className="mt-6 text-lg text-slate-700 leading-relaxed whitespace-pre-line">{state.winner.story}</p>
                {state.winner.message && <blockquote className="mt-6 border-l-4 border-launch pl-5 text-left text-navy font-medium italic">{state.winner.message}</blockquote>}
                {(state.winner.winner_photos || []).length > 0 && (
                  <div className="mt-8 grid sm:grid-cols-2 gap-4">{state.winner.winner_photos.map((p, i) => <img key={i} src={media(p)} alt={`Installation ${i + 1}`} className="rounded-xl object-cover w-full h-56" />)}</div>
                )}
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + "/veteran-hvac-giveaway")}`}
                    target="_blank" rel="noopener noreferrer" data-testid="giveaway-share-fb"
                    className="inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0f6ae0] text-white font-bold py-3 px-6 rounded-md transition-colors">
                    <Share2 className="h-4 w-4" /> Share on Facebook
                  </a>
                  {state.winner.winner_card_url && (
                    <a href={media(state.winner.winner_card_url)} target="_blank" rel="noopener noreferrer" data-testid="giveaway-download-card"
                      className="inline-flex items-center gap-2 border border-navy/30 text-navy font-semibold py-3 px-6 rounded-md hover:bg-slate-50 transition-colors">
                      View Shareable Card
                    </a>
                  )}
                </div>
              </>
            ) : (
              <>
                <Award className="h-12 w-12 text-launch mx-auto" />
                <h2 className="mt-4 text-3xl font-extrabold text-navy">Winner Announcement Coming Veterans Day</h2>
                <p className="mt-3 text-slate-600">We're preparing to honor the veteran who will receive a brand-new HVAC system and professional installation. Check back on November 11.</p>
              </>
            )}
          </div>
        </section>
      )}

      {/* NOMINEES */}
      {["voting", "review"].includes(phase) && (
        <section id="nominees" className="py-14 md:py-20" data-testid="giveaway-nominees-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-navy">{phase === "voting" ? "Vote for a Veteran" : "The Nominees"}</h2>
                <p className="text-slate-500 mt-1">{phase === "voting" ? "One vote per verified email. Choose the veteran whose story moves you." : "Voting has closed — thank you to everyone who participated."}</p>
              </div>
              {phase === "voting" && <button onClick={() => setNomOpen(true)} className="inline-flex items-center gap-2 bg-navy text-white font-semibold py-2.5 px-5 rounded-md text-sm"><Heart className="h-4 w-4" /> Nominate a Veteran</button>}
            </div>
            {nominees.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500" data-testid="giveaway-no-nominees">
                No approved nominees yet. {phase === "voting" && "Be the first to nominate a deserving veteran!"}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {nominees.map((n) => <NomineeCard key={n.id} n={n} canVote={phase === "voting"} onVote={setVoteFor} />)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* PRE/TEASER info */}
      {["pre", "teaser"].includes(phase) && (
        <section className="py-14 md:py-20" data-testid="giveaway-info-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center">
            <img src="/giveaway/dates.jpg" alt="Nominations & voting open October 1–31, 2026" className="rounded-2xl shadow-lg w-full object-cover" />
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-navy">Nominate a Veteran Who Deserves a New HVAC System</h2>
              <p className="mt-4 text-slate-700 leading-relaxed">This Veterans Day, we're giving back to one veteran in our community with a brand-new HVAC system and professional installation. Nominations and public voting open <strong>October 1, 2026</strong> and close <strong>October 31</strong>. The winner will be announced on <strong>Veterans Day, November 11</strong>.</p>
              <ul className="mt-6 space-y-3">
                {["Nominate a veteran and share their story", "The community votes for who should win", "One verified email = one vote", "Winner revealed on Veterans Day"].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="h-5 w-5 text-launch flex-shrink-0 mt-0.5" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* PRIZE + RULES */}
      <section className="py-12 bg-[#0a1730] text-white" data-testid="giveaway-prize-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShieldCheck className="h-10 w-10 text-launch mx-auto" />
          <h2 className="mt-3 text-2xl font-bold">The Prize: {rules?.prize_text || state?.prize_text || "One new HVAC system with professional installation"}</h2>
          <p className="mt-3 text-slate-300 text-sm max-w-2xl mx-auto">{rules?.prize_exclusions}</p>
          <details className="mt-6 text-left bg-white/5 rounded-lg p-5" data-testid="giveaway-rules">
            <summary className="cursor-pointer font-semibold text-launch">Official Giveaway Rules</summary>
            <p className="mt-3 text-sm text-slate-300 whitespace-pre-line">{rules?.official_rules}</p>
          </details>
        </div>
      </section>

      <CtaBlock heading="Comfort for Those Who Gave So Much" />

      <VoteDialog nominee={voteFor} open={!!voteFor} onClose={() => setVoteFor(null)} />
      <NominateDialog open={nomOpen} onClose={() => setNomOpen(false)} onDone={refetch} />
    </>
  );
}
